/**
 * Servicio de configuración centralizada
 * Basado en JsonRequest.cs
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface Config {
  baseUrl: string;
  baseUrlLLM: string;
  client: string;
  assistantID: string;
  testingPhase: boolean;
  testingWebSocket: boolean;
  useApolearn: boolean; // Si es true, requiere token de auth; si es false, no requiere
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  userFullName?: string;
  userFirstname?: string;
  userLastname?: string;
  userID?: number;
  promptReference?: string;
  obtainFullPromptURL?: string;
  promptAccessToken?: string;
  promptRefreshToken?: string;
  fullPrompt?: string;
  practiceMode?: string; // Modo de práctica (conversacional, estudio, examen, etc.)
  accent?: string; // Acento seleccionado (Francia, Canadá, Australia, Bélgica)
  // Nuevos parámetros de personalización
  personalityType?: string; // Personalidad de Galia (Amigable, Sarcástica, etc.)
  functionalityType?: string; // Funcionalidad de Galia (Profe, Conversadora)
  mcerLevel?: string; // Nivel MCER del estudiante (A1, A2, B1, B2, C1, C2)
}

class ConfigService {
  private config: Config = {
    baseUrl: '',
    baseUrlLLM: '',
    client: 'alianzafrancesa',
    assistantID: '',
    testingPhase: true,
    testingWebSocket: true,
    useApolearn: false, // Por defecto: no apolearn (no requiere auth)
  };

  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 30000,
    });

    // Interceptor para agregar token Bearer
    this.axiosInstance.interceptors.request.use(
      (config) => {
        if (this.config.accessToken) {
          config.headers.Authorization = `Bearer ${this.config.accessToken.trim()}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores 401 (token expirado)
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && this.config.refreshToken) {
          // Intentar refrescar el token
          try {
            await this.refreshToken();
            // Reintentar la petición original
            return this.axiosInstance.request(error.config);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  initialize(config: Partial<Config>): void {
    this.config = { ...this.config, ...config };
    
    // Construir URLs
    if (this.config.testingPhase) {
      // En local/desarrollo, siempre usar servidor de desarrollo
      this.config.baseUrl = 'https://alianzafrancesa.niimx.us/';
      this.config.baseUrlLLM = 'https://llm-dev.aitopstaff.com';
    } else {
      // En producción, usar servidor de producción
      // baseUrl ya no se usa - todas las llamadas se hacen a través de baseUrlLLM
      this.config.baseUrl = '';
      this.config.baseUrlLLM = 'https://llm.aitopstaff.com';
    }

    this.config.obtainFullPromptURL = `${this.config.baseUrlLLM}/api/v1/bot/get_full_prompt/`;
  }

  getConfig(): Config {
    return { ...this.config };
  }

  setAccessToken(token: string): void {
    this.config.accessToken = token;
  }

  setRefreshToken(token: string): void {
    this.config.refreshToken = token;
  }

  setPromptAccessToken(token: string): void {
    this.config.promptAccessToken = token;
  }

  setPromptRefreshToken(token: string): void {
    this.config.promptRefreshToken = token;
  }

  setUserInfo(email: string, fullName: string, userID?: number, firstname?: string, lastname?: string): void {
    this.config.email = email;
    this.config.userFullName = fullName;
    this.config.userFirstname = firstname;
    this.config.userLastname = lastname;
    if (userID) {
      this.config.userID = userID;
    }
  }

  /**
   * Actualizar promptReference dinámicamente
   */
  setPromptReference(promptReference: string): void {
    const previousReference = this.config.promptReference;
    if (previousReference === promptReference) {
      return;
    }
    this.config.promptReference = promptReference;
    // Limpiar el prompt completo para forzar recarga con nuevo reference
    this.config.fullPrompt = undefined;
  }

  /**
   * Actualizar avatarType/mode para el avatar
   */
  setAvatarType(avatarType: string): void {
    // Guardar en una propiedad adicional si es necesario
    (this.config as any).avatarType = avatarType;
  }

  getAvatarType(): string | undefined {
    return (this.config as any).avatarType;
  }

  /**
   * Guardar modo de práctica y acento para configuración de voz
   */
  setPracticeModeAndAccent(mode: string, accent: string): void {
    this.config.practiceMode = mode;
    this.config.accent = accent;
  }

  getPracticeMode(): string | undefined {
    return this.config.practiceMode;
  }

  getAccent(): string | undefined {
    return this.config.accent;
  }

  /**
   * Guardar personalidad de Galia
   */
  setPersonalityType(personalityType: string): void {
    this.config.personalityType = personalityType;
    this.savePersonalizationToCache();
  }

  getPersonalityType(): string | undefined {
    return this.config.personalityType;
  }

  /**
   * Guardar funcionalidad de Galia
   */
  setFunctionalityType(functionalityType: string): void {
    this.config.functionalityType = functionalityType;
    this.savePersonalizationToCache();
  }

  getFunctionalityType(): string | undefined {
    return this.config.functionalityType;
  }

  /**
   * Guardar nivel MCER del estudiante
   */
  setMCERLevel(mcerLevel: string): void {
    this.config.mcerLevel = mcerLevel;
    this.savePersonalizationToCache();
  }

  getMCERLevel(): string | undefined {
    return this.config.mcerLevel;
  }

  /**
   * Guardar toda la configuración de personalización en cache (localStorage)
   */
  private savePersonalizationToCache(): void {
    try {
      const personalization = {
        personalityType: this.config.personalityType,
        functionalityType: this.config.functionalityType,
        mcerLevel: this.config.mcerLevel,
      };
      localStorage.setItem('galia_personalization', JSON.stringify(personalization));
    } catch {
      // Error silencioso al guardar personalización en caché
    }
  }

  /**
   * Cargar configuración de personalización desde cache (localStorage)
   */
  loadPersonalizationFromCache(): void {
    try {
      const cached = localStorage.getItem('galia_personalization');
      if (cached) {
        const personalization = JSON.parse(cached);
        this.config.personalityType = personalization.personalityType;
        this.config.functionalityType = personalization.functionalityType;
        this.config.mcerLevel = personalization.mcerLevel;
      }
    } catch {
      // Error silencioso al cargar personalización desde caché
    }
  }

  // Métodos HTTP helpers
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, {
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    });
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, {
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    });
    return response.data;
  }

  async getWithBearerToken<T>(url: string, token: string): Promise<T> {
    const response = await axios.get<T>(url, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
      },
    });
    return response.data;
  }

  async postWithBearerToken<T>(url: string, token: string, data: any): Promise<T> {
    const response = await axios.post<T>(url, data, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  async patchWithBearerToken<T>(url: string, token: string, data: any): Promise<T> {
    const response = await axios.patch<T>(url, data, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  private async refreshToken(): Promise<void> {
    if (!this.config.refreshToken || !this.config.baseUrlLLM) {
      throw new Error('No hay refresh token disponible');
    }

    // Usar baseUrlLLM en lugar de baseUrl (ya no se usa mentoraibot.com)
    const refreshUrl = `${this.config.baseUrlLLM}/api/v1/auth/refresh/`;
    const response = await axios.post<{ access: string }>(refreshUrl, {
      refresh: this.config.refreshToken,
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.config.accessToken = response.data.access;
  }

  /**
   * Login para obtener token de acceso al prompt
   * Basado en promptApiConection() del código C# original
   */
  async loginForPrompt(): Promise<void> {
    const loginUrl = `${this.config.baseUrlLLM}/api/v1/login/`;
    
    const response = await axios.post<{
      tokens: {
        access: string;
        refresh: string;
      };
    }>(loginUrl, {
      username: 'llmprompts',
      password: 'Z-:sMOL6L_',
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.config.promptAccessToken = response.data.tokens.access;
    this.config.promptRefreshToken = response.data.tokens.refresh;
  }

  /**
   * Refrescar token de acceso al prompt
   */
  private async refreshPromptToken(): Promise<void> {
    if (!this.config.promptRefreshToken || !this.config.baseUrlLLM) {
      // Si no hay refresh token, hacer login de nuevo
      await this.loginForPrompt();
      return;
    }

    const refreshUrl = `${this.config.baseUrlLLM}/api/v1/auth/refresh/`;
    try {
      const response = await axios.post<{ access: string }>(refreshUrl, {
        refresh: this.config.promptRefreshToken,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      this.config.promptAccessToken = response.data.access;
    } catch {
      // Si el refresh falla, hacer login de nuevo
      await this.loginForPrompt();
    }
  }

  // IMPORTANTE: Llamado al prompt principal
  async getFullPrompt(promptReference: string, context: any): Promise<string> {
    if (!this.config.obtainFullPromptURL) {
      throw new Error('Configuración de prompt no disponible');
    }

    // Asegurar que tenemos el token antes de hacer la petición
    if (!this.config.promptAccessToken) {
      await this.loginForPrompt();
    }

    const requestData = {
      ref: promptReference,
      context,
    };

    try {
      const response = await this.postWithBearerToken<{ payload: string }>(
        this.config.obtainFullPromptURL,
        this.config.promptAccessToken!,
        requestData
      );

      this.config.fullPrompt = response.payload;
      return response.payload;
    } catch (error: any) {
      // Si el error es 401 (token expirado), intentar refrescar y reintentar
      if (error.response?.status === 401) {
        try {
          await this.refreshPromptToken();
          // Reintentar la petición con el nuevo token
          const retryResponse = await this.postWithBearerToken<{ payload: string }>(
            this.config.obtainFullPromptURL!,
            this.config.promptAccessToken!,
            requestData
          );
          this.config.fullPrompt = retryResponse.payload;
          return retryResponse.payload;
        } catch (retryError) {
          throw retryError;
        }
      }

      if (error.response?.status === 500) {
        throw new Error(`El prompt "${promptReference}" no existe en el backend. Por favor verifica que el prompt esté configurado correctamente.`);
      }
      
      // Re-lanzar otros errores
      throw error;
    }
  }

  /**
   * Actualizar el prompt completo después de obtenerlo
   */
  setFullPrompt(fullPrompt: string): void {
    this.config.fullPrompt = fullPrompt;
  }
}

export const configService = new ConfigService();
export default configService;


